import QuizAnswerInput from '@/components/activities/elements/edit/QuizElementEdit/QuizAnswerInput';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import RoundedButton from '@/components/ui/custom/buttons/RoundedButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import TextAreaInput from '@/components/ui/custom/dataInput/TextAreaInput';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import Modal from '@/components/ui/custom/modals/Modal';
import { LoadingIcon } from '@/components/icons';
import useScroll from '@/data/hook/useScroll';
import {
  createQuizQuestionIntoActivity,
  QuizAnswer,
  updateQuizQuestionIntoActivity,
} from '@/data/services/activityElement/quizServices';
import { toQuizAnswersPayload } from '@/utils/questions/legacyQuizAnswers';
import { resolveQuestionInActivity } from '@/utils/questions/resolveQuestionInActivity';
import {
  createActivity,
  getActivity,
  updateActivityFromLesson,
} from '@/data/services/_deprecated/activityServices';
import { SectionRouteParams } from '@/data/services/sectionServices';
import { ActivityTypeEnum } from '@/models/_deprecated/ActivityElement';
import {
  QuizActivityElement,
  QuizAnswersType,
} from '@/models/_deprecated/ActivityElement';
import { QuizActivity } from '@/models/_deprecated/ActivityProgress';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { ActivityQuiz } from '@/utils/editorjs/activity-block/adapter/activityToQuizAdapter';
import { getQuestionErrorMessage } from '@/utils/questions/getQuestionErrorMessage';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import alert from '@/utils/shared/alert';
import { useMutation } from '@tanstack/react-query';
import { Fragment, useRef } from 'react';
import {
  Control,
  Controller,
  SubmitHandler,
  useController,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { v4 as uuidV4 } from 'uuid';

type Props = {
  sectionParams: SectionRouteParams;
  activityId?: number;
  onClose: () => void;
  onSaveActivity: (activity: ActivityQuiz) => void;
  isVisible?: boolean;
};

export default function SectionActivityFormModal({
  sectionParams,
  activityId,
  onClose,
  onSaveActivity,
  isVisible = false,
}: Props) {
  const [tQuizForm] = useTranslation('translation', {
    keyPrefix: 'section.quizActivityForm',
  });
  const title = activityId ? tQuizForm('editTitle') : tQuizForm('createTitle');
  return (
    <Modal
      className={{ base: 'max-w-3xl' }}
      visible={isVisible}
      onClose={onClose}
    >
      <div
        className={twJoin(
          'flex flex-col gap-3.5 max-h-[80vh] overflow-y-auto',
          scrollBarYClassName,
        )}
      >
        <Text
          text={title}
          as="h3"
          className="text-primary text-24"
          format="rubik-500"
        />
        <QuizActivityForm
          sectionParams={sectionParams}
          activityId={activityId}
          onClose={onClose}
          onSaveActivity={onSaveActivity}
        />
      </div>
    </Modal>
  );
}

type QuizForm = Omit<QuizActivity, 'quizAnswers' | 'hasStepper'> & {
  quizAnswers: QuizAnswer[];
};

type QuizActivityFormProps = {
  sectionParams: SectionRouteParams;
  activityId?: number;
  onClose: () => void;
  onSaveActivity: (activity: ActivityQuiz) => void;
};

function QuizActivityForm({
  sectionParams,
  activityId,
  onClose,
  onSaveActivity,
}: QuizActivityFormProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const [tQuizForm] = useTranslation('translation', {
    keyPrefix: 'section.quizActivityForm',
  });

  const defaultValuesRef = useRef<QuizForm>({} as QuizForm);

  const defaultValues = defaultValuesRef.current;

  const mutationConfig = {
    onSuccess: (data: ActivityQuiz) => {
      onSaveActivity(data);
      onClose();
    },
    onError: (error: Error) => {
      alert.error(getQuestionErrorMessage(error));
    },
  };

  const { mutate: create, isLoading: isCreating } = useMutation(
    handleCreate,
    mutationConfig,
  );
  const { mutate: update, isLoading: isUpdating } = useMutation(
    handleUpdate,
    mutationConfig,
  );

  const isRequesting = isCreating || isUpdating;

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isLoading },
  } = useForm<QuizForm>({
    async defaultValues() {
      if (!activityId) return {} as QuizForm;
      const activity = await getActivity(activityId);
      const quiz = activity.questions[0] as QuizActivityElement;
      const defaultValues = {
        ...quiz,
        quizAnswers: quiz?.quizAnswers.sort(
          (a, b) => (a.id ?? 0) - (b.id ?? 0),
        ),
      } as QuizForm;
      defaultValuesRef.current = defaultValues;
      return defaultValues;
    },
  });

  async function handleCreate(quizPayload: QuizForm) {
    const activity = await createActivity(sectionParams.lessonId, {
      name: quizPayload.question,
    });
    const quiz = await createQuizQuestionIntoActivity(activity.id, {
      ...quizPayload,
      quizAnswers: toQuizAnswersPayload(
        quizPayload.quizAnswers,
        QuizAnswersType.TEXT,
      ),
      answerType: QuizAnswersType.TEXT,
      identifier: uuidV4(),
      order: 1,
      type: ActivityTypeEnum.QUIZ,
      hasStepper: false,
    });

    return {
      ...activity,
      questions: [quiz],
    };
  }

  async function handleUpdate(quizPayload: QuizForm) {
    if (!activityId) throw new Error('Activity not found');

    const activityChanges = buildChangedObject(
      { name: defaultValues.question },
      { name: quizPayload.question },
    );
    const quizChanges = buildChangedObject(defaultValues, quizPayload);
    const activity = await updateActivityFromLesson(
      { activityId, lessonId: sectionParams.lessonId },
      activityChanges,
    );
    const target = resolveQuestionInActivity(activity, quizPayload);
    const quiz = await updateQuizQuestionIntoActivity(activity.id, target.id, {
      ...quizChanges,
      quizAnswers: toQuizAnswersPayload(
        quizPayload.quizAnswers,
        QuizAnswersType.TEXT,
        target.quizAnswers,
      ),
    });

    return {
      ...activity,
      questions: [quiz],
    };
  }
  const onSubmit: SubmitHandler<QuizForm> = data => {
    if (!activityId) {
      return create(data);
    }
    return update(data);
  };

  const defaultLabelClassName = 'text-14 leading-none';

  if (isLoading) {
    return <LoadingIcon className="w-10 h-10 text-primary" />;
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-3.5 items-start pt-3.5"
    >
      <div className="flex flex-col gap-2.5 w-full">
        <TextInput
          testId="questionInput"
          label={tQuizForm('question')}
          {...register('question', {
            required: t('form.required', {
              field: tQuizForm('question').toLowerCase(),
            }),
          })}
          errorLabelText={errors.question?.message}
        />
        <TextAreaInput
          className={{ label: defaultLabelClassName }}
          label={tQuizForm('content')}
          {...register('content')}
        />
      </div>

      <QuizAnswersForm control={control} />

      <hr className="border-primary w-full" />

      <TextAreaInput
        label={tQuizForm('explain')}
        className={{ base: 'w-full', label: defaultLabelClassName }}
        {...register('reason', {
          required: t('form.required', {
            field: tQuizForm('explain').toLowerCase(),
          }),
        })}
        testId="explainInput"
        errorLabelText={errors.reason?.message}
      />

      <div className="flex w-full gap-6 justify-center items-center mt-2.5">
        <RoundedButton
          type="button"
          text={t('cancel')}
          color="neutral"
          onClick={onClose}
          disabled={isRequesting}
        />
        <RoundedButton
          type="submit"
          text={t('save')}
          color="gradient"
          loading={isRequesting}
        />
      </div>
    </form>
  );
}

type QuizAnswersFormProps = {
  control: Control<QuizForm>;
};

function QuizAnswersForm({ control }: QuizAnswersFormProps) {
  const [tQuizForm] = useTranslation('translation', {
    keyPrefix: 'section.quizActivityForm',
  });

  const {
    fieldState: { error },
  } = useController({ control, name: 'quizAnswers' });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'quizAnswers',
    rules: {
      validate(answers = []) {
        if (answers.length < 2) {
          return tQuizForm('minAnswer');
        }
        const correctAnswerCount = answers.reduce(
          (count, { isCorrect }) => (isCorrect ? count + 1 : count),
          0,
        );

        if (correctAnswerCount > 1) {
          return tQuizForm('onlyOneCorrectAnswer');
        }
        if (correctAnswerCount <= 0) {
          return tQuizForm('atLeastOneCorrectAnswer');
        }

        return true;
      },
    },
  });

  const { ref, down } = useScroll<HTMLDivElement>();

  const onAppend = async () => {
    await append({
      answer: 'Nova resposta',
      isCorrect: false,
      answerVerifier: uuidV4(),
      id: NaN,
    });

    down();
  };
  return (
    <Fragment>
      <Text className="text-14" text={tQuizForm('answers')} />
      <ConditionalRenderer condition={error?.root?.message}>
        <Text className="text-error text-14" text={error?.root?.message} />
      </ConditionalRenderer>

      <div
        ref={ref}
        className={twJoin(
          'flex flex-col gap-3.5 w-full max-h-64 overflow-y-auto',
          scrollBarYClassName,
        )}
      >
        {fields.map((field, index) => (
          <Controller
            key={field.id}
            control={control}
            name={`quizAnswers.${index}`}
            render={({ field: { onChange, value } }) => (
              <QuizAnswerInput
                index={index}
                value={value}
                onChange={onChange}
                onRemove={() => remove(index)}
              />
            )}
          />
        ))}
      </div>

      <MainButton
        text={tQuizForm('newAnswer')}
        color="secondary"
        onClick={onAppend}
      />
    </Fragment>
  );
}
